"use client";
import React, { createContext, useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {BASE_URL} from '../config/apiConfig';
 
const ProfileContext = createContext();
 
export const ProfileProvider = ({ children }) => {
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
 
  const token =
    typeof window !== "undefined" ? localStorage.getItem("auth_token") : null;
  const userId =
    typeof window !== "undefined" ? localStorage.getItem("user_id") : null;
 
  // Fetch profile on mount
  useEffect(() => {
    const fetchProfile = async () => {
      if (!token || !userId) {
        setLoading(false);
        return;
      }
 
      try {
        const res = await fetch(`${BASE_URL}/users/${userId}`, {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        });
        const json = await res.json();
        if (json.status === 200) setProfile(json.data);
      } catch (err) {
        console.error("API error:", err);
      } finally {
        setLoading(false);
      }
    };
 
    fetchProfile();
  }, [token, userId]);
 
  // Update profile
const updateProfile = async (formData) => {
  if (!token || !userId)
    return { success: false, message: "Missing token or user ID." };
 
  try {
    const res = await fetch(`${BASE_URL}/user/update-profile/${userId}`, {
      method: "POST",
      headers: { Authorization: `Bearer ${token}` },
      body: formData,
    });
 
    const json = await res.json();
 
    if (json.status === 200) {
      // Ensure data exists
      const updatedData = json.data || {};
 
      // ✅ Update context
      setProfile(updatedData);
 
      // ✅ Update localStorage
      localStorage.setItem("auth_user", JSON.stringify(updatedData));
 
      return {
        success: true,
        message: json.message || "Profile updated",
        data: updatedData, // same key as your original code
      };
    } else {
      return { success: false, message: json.message || "Update failed" };
    }
  } catch (err) {
    return { success: false, message: "Request failed" };
  }
};
 
 
 
 
  // Send OTP (email or mobile)
  const sendOtp = async (payload, type = "email") => {
    if (!userId) return { status: 500, message: "Missing user ID" };
 
    let endpoint = type === "mobile"
      ? `${BASE_URL}/user/send-otp/${userId}`
      : `${BASE_URL}/user/send-otp/${userId}`;
 
    try {
      const res = await fetch(endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload), // { email } or { mobile }
      });
      return await res.json();
    } catch (err) {
      console.error("Send OTP error:", err);
      return { status: 500, message: "Failed to send OTP" };
    }
  };
 
  // Verify Email OTP
  const verifyEmailOtp = async ({ email, otp }) => {
    if (!userId) return { status: 500, message: "Missing user ID" };
    if (!otp || !email) return { status: 400, message: "Email and OTP required" };
 
    try {
      const payload = { email, otp };
      const res = await fetch(`${BASE_URL}/user/verify-otp/${userId}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
 
      const json = await res.json();
 
      if (json.status === 200) {
        setProfile((prev) => ({ ...prev, email }));
      }
 
      return json;
    } catch (err) {
      console.error("Verify Email OTP error:", err);
      return { status: 500, message: "Failed to verify email OTP" };
    }
  };
 
  // Verify Mobile OTP (different API)
  const verifyMobileOtp = async ({ mobile, otp }) => {
    if (!userId) return { status: 500, message: "Missing user ID" };
    if (!otp || !mobile) return { status: 400, message: "Mobile and OTP required" };
 
    try {
      const payload = { mobile, otp };
      const res = await fetch(`${BASE_URL}/user/verify-mobile-otp/${userId}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
 
      const json = await res.json();
 
      if (json.status === 200) {
        setProfile((prev) => ({ ...prev, mobile }));
      }
 
      return json;
    } catch (err) {
      console.error("Verify Mobile OTP error:", err);
      return { status: 500, message: "Failed to verify mobile OTP" };
    }
  };
 
  // Change password
  const changePassword = async (oldPassword, newPassword) => {
    if (!userId) return { status: 500, message: "Missing user ID" };
 
    try {
      const res = await fetch(
        `${BASE_URL}/user/change-password/${userId}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            password: oldPassword,
            confirm_password: newPassword,
          }),
        }
      );
      return await res.json();
    } catch (err) {
      console.error("Password change error:", err);
      return { status: 500, message: "Failed to change password" };
    }
  };
 
  // Delete account
  const deleteAccount = async () => {
    if (!userId) return { status: 500, message: "Missing user ID" };
 
    try {
      const res = await fetch(`${BASE_URL}/users/${userId}`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          delete_audit_id: 1, // 👈 This goes into the DELETE body
        }),
      });
 
      const json = await res.json();
 
      if (json.status === 200) {
        localStorage.removeItem("auth_token");
        localStorage.removeItem("user_id");
        setProfile(null);
        navigate("/"); // 👈 Redirect to home page
      }
 
      return json;
    } catch (err) {
      console.error("Delete error:", err);
      return { status: 500, message: "Failed to delete account" };
    }
  };
 
  return (
    <ProfileContext.Provider
      value={{
        profile,
        setProfile,
        loading,
        updateProfile,
        sendOtp,
        verifyEmailOtp,
        verifyMobileOtp,
        changePassword,
        deleteAccount,
      }}
    >
      {children}
    </ProfileContext.Provider>
  );
};
 
export const useProfile = () => useContext(ProfileContext);
 